import axios from "axios";

import { dispatch, getState } from "../state/store";
import { setIsLoggedIn } from "../state/ducks";
import { selectIsLoggedIn } from "../state/selectors";

import log from "./logger/log";
import { awsAuthErrorTypes, strCode } from "../consts";
import { notificationService, t } from "./index";
import awsAmplifyService from "./aws/amplify/awsAmplifyService";
import authService from "./auth/authService";

const URLS_AUTH_WHITELIST = ["/version.txt"];

let controller = new AbortController();

class NetworkError extends Error {
  toJSON() {
    return {
      message: this.message,
      stack: this.stack
    };
  }
}

const get = (url, requestConfig) => makeRequest("get", url, requestConfig);

const post = (url, requestConfig = {}) => makeRequest("post", url, requestConfig);

const put = (url, requestConfig = {}) => makeRequest("put", url, requestConfig);

const remove = (url, requestConfig = {}) => makeRequest("delete", url, requestConfig);

const getAuthHeaders = async () => {
  const isUserLoggedIn = selectIsLoggedIn()(getState());

  const headers = {};

  try {
    const session = await awsAmplifyService.currentSession();

    headers.authorization = `Bearer ${session?.idToken?.jwtToken}`;
  } catch (err) {
    if (
      isUserLoggedIn &&
      (err === awsAuthErrorTypes.NO_CURRENT_USER ||
        err.name === awsAuthErrorTypes.NOT_AUTHORIZED_EXCEPTION)
    ) {
      notificationService.showError({
        title: t(strCode.REFRESH_TOKEN_EXPIRED_TITLE),
        message: null
      });

      authService.logOut();
    }
  }

  return headers;
};

const makeRequest = async (method, url, requestConfig = {}) => {
  try {
    const authHeaders = await getAuthHeaders();

    if (!authHeaders.authorization && !URLS_AUTH_WHITELIST.includes(url)) {
      return;
    }

    log.debug(`httpClient: making '${method}' request to '${url}'`);
    let config = { method: method, url: url };

    config = {
      ...config,
      ...requestConfig,
      signal: controller.signal,
      headers: { ...config?.headers, ...requestConfig?.headers, ...authHeaders }
    };

    return await request(config);
  } catch (error) {
    const errorDetails = getErrorDetails(error);
    log.error(`httpClient: Call ${url} error. ${error.message}`, errorDetails);

    const isUnauthError = errorDetails?.status === 401;
    const isCancelled = axios.isCancel(error);

    if (isCancelled) {
      return;
    }

    if (error.message && error.message === "Network Error" && !isUnauthError && !isCancelled) {
      throw new NetworkError(error.message);
    }

    if (isUnauthError) {
      handleUnauthorizedErrors();
    } else {
      throw error;
    }
  }
};

const getErrorDetails = (error) => {
  if (!error?.isAxiosError) {
    return error;
  }

  const headers = error.config?.headers ? Object.keys(error.config.headers) : [];

  const response = error.response;

  return {
    message: error.message,
    response: response?.data,
    headers: headers,
    status: response?.status
  };
};

const handleUnauthorizedErrors = async () => {
  controller.abort();
  const isLoggedIn = selectIsLoggedIn()(getState());

  if (isLoggedIn) {
    await authService.logOut();

    notificationService.showError({
      title: "",
      message: t(strCode.SESSION_CLOSED_MESSAGE)
    });

    dispatch(setIsLoggedIn(false));

    controller = new AbortController();
  }
};

const request = (requestConfig) => axios.request(requestConfig);

const httpClient = {
  get,
  post,
  put,
  remove,
  makeRequest
};

export default httpClient;
